import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAdminAuth } from "../context/AdminAuthContext";
import PageMeta from "../components/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAME_KEY = "rm_community_display_name";

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function CommentNode({ thread, displayName, onReply, onFlag }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);

  const submitReply = async () => {
    const text = replyText.trim();
    if (!text || !displayName.trim()) return;
    setPosting(true);
    try {
      await onReply(thread.id, text);
      setReplyText("");
      setReplyOpen(false);
    } finally {
      setPosting(false);
    }
  };

  return (
    <article
      className="rm-cm-card"
      data-testid={`community-comment-${thread.id}`}
    >
      <header className="rm-cm-card-head">
        <span className="rm-cm-name" data-testid="community-comment-author">
          {thread.display_name}
        </span>
        <span className="rm-cm-time">{formatTime(thread.created_at)}</span>
      </header>
      <p className="rm-cm-body" data-testid="community-comment-body">
        {thread.content}
      </p>
      <div className="rm-cm-actions">
        <button
          type="button"
          className="rm-cm-link"
          onClick={() => setReplyOpen((v) => !v)}
          data-testid={`community-reply-toggle-${thread.id}`}
        >
          {replyOpen ? "Cancel" : "Reply"}
        </button>
        <button
          type="button"
          className="rm-cm-link rm-cm-link-muted"
          onClick={() => onFlag(thread.id)}
          data-testid={`community-flag-${thread.id}`}
        >
          Flag
        </button>
      </div>

      {replyOpen && (
        <div className="rm-cm-reply-box">
          <textarea
            className="rm-cm-textarea rm-cm-reply-textarea"
            placeholder={
              displayName.trim()
                ? "Write a reply…"
                : "Set your display name above first…"
            }
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            maxLength={2000}
            rows={3}
            data-testid={`community-reply-textarea-${thread.id}`}
          />
          <div className="rm-cm-reply-actions">
            <button
              type="button"
              className="rm-btn-solid rm-cm-btn-sm"
              disabled={
                posting || !replyText.trim() || !displayName.trim()
              }
              onClick={submitReply}
              data-testid={`community-reply-submit-${thread.id}`}
            >
              {posting ? "Posting…" : "Post reply"}
            </button>
          </div>
        </div>
      )}

      {(thread.replies || []).length > 0 && (
        <div className="rm-cm-replies">
          {thread.replies.map((r) => (
            <div
              key={r.id}
              className="rm-cm-reply"
              data-testid={`community-reply-${r.id}`}
            >
              <header className="rm-cm-card-head">
                <span className="rm-cm-name">{r.display_name}</span>
                <span className="rm-cm-time">{formatTime(r.created_at)}</span>
              </header>
              <p className="rm-cm-body">{r.content}</p>
              <div className="rm-cm-actions">
                <button
                  type="button"
                  className="rm-cm-link rm-cm-link-muted"
                  onClick={() => onFlag(r.id)}
                  data-testid={`community-flag-reply-${r.id}`}
                >
                  Flag
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export default function Community() {
  const { admin } = useAdminAuth();
  const isAdmin = admin && typeof admin === "object";
  const [displayName, setDisplayName] = useState("");
  const [content, setContent] = useState("");
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Restore the last-used display name so returning users don't have to
  // retype it. The name is local-only — never sent without a fresh post.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(NAME_KEY) || "";
      if (saved) setDisplayName(saved);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/comments?limit=100`);
        if (cancelled) return;
        setThreads(res.data?.comments || []);
      } catch (e) {
        if (cancelled) return;
        setError("We couldn't load the community right now. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedName = useMemo(() => displayName.trim(), [displayName]);
  const trimmedContent = useMemo(() => content.trim(), [content]);
  const canPost = trimmedName.length > 0 && trimmedContent.length > 0 && !posting;

  const persistName = (name) => {
    try {
      localStorage.setItem(NAME_KEY, name);
    } catch {
      /* ignore */
    }
  };

  const submitTopLevel = async (e) => {
    e.preventDefault();
    if (!canPost) return;
    setPosting(true);
    setError("");
    setInfo("");
    try {
      const res = await axios.post(`${API}/comments`, {
        display_name: trimmedName,
        content: trimmedContent,
      });
      const newComment = { ...res.data, replies: [] };
      setThreads((t) => [newComment, ...t]);
      setContent("");
      persistName(trimmedName);
      setInfo("Posted. Thank you for sharing.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not post your comment. Try again.";
      setError(typeof msg === "string" ? msg : "Could not post your comment.");
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async (parentId, text) => {
    setError("");
    setInfo("");
    try {
      const res = await axios.post(`${API}/comments/${parentId}/replies`, {
        display_name: trimmedName,
        content: text,
      });
      setThreads((arr) =>
        arr.map((t) =>
          t.id === parentId
            ? { ...t, replies: [...(t.replies || []), res.data] }
            : t,
        ),
      );
      persistName(trimmedName);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Could not post your reply. Try again.";
      setError(typeof msg === "string" ? msg : "Could not post your reply.");
    }
  };

  const flagComment = async (id) => {
    setError("");
    setInfo("");
    try {
      await axios.post(`${API}/comments/${id}/flag`);
      setInfo(
        "Thanks — flagged for review. Repeated flags will hide a comment automatically.",
      );
    } catch {
      setError("Could not flag this comment. Try again.");
    }
  };

  return (
    <main className="relative min-h-screen" data-testid="community-page">
      <PageMeta
        title="The Community Room"
        description="A public, anonymous room for testimony, prayer, and honest questions. Share what the Matrix has met in your walk, and pray with others who are walking the ladder too."
        path="/community"
      />
      <div className="rm-cm-bg" aria-hidden="true" />

      <header className="relative z-10 px-6 sm:px-12 md:px-20 py-10 flex items-center justify-between">
        <Link
          to="/"
          className="font-cinzel text-xs sm:text-sm tracking-[0.28em] uppercase text-[#FFE7A0]/85 hover:text-[#FFE7A0]"
          data-testid="community-home-link"
        >
          ← The Restoration Matrix
        </Link>
        {isAdmin && (
          <Link
            to="/admin"
            className="font-cinzel text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#FFE7A0]/75 hover:text-[#FFE7A0]"
            data-testid="community-admin-link"
          >
            Admin
          </Link>
        )}
      </header>

      <section className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center"
        >
          <div
            className="font-cinzel text-[10px] sm:text-xs tracking-[0.32em] uppercase text-[#FFE7A0]/80 mb-6"
            data-testid="community-overline"
          >
            The Community Room
          </div>
          <h1
            className="font-serif-display italic text-4xl sm:text-5xl lg:text-6xl text-[#FFE7A0] tracking-tight leading-[1.1]"
            data-testid="community-title"
          >
            Speak from where the work has met you.
          </h1>
          <p
            className="mt-8 max-w-2xl mx-auto text-base sm:text-lg leading-[1.7] text-[#F0E6D2]/85 font-body"
            data-testid="community-blurb"
          >
            A public room. No accounts, no logins — just a name you choose and
            a few honest words. Share testimony, ask the question you've been
            holding, or pray for someone walking the same rung. Be kind. Be
            specific. Be real.
          </p>
        </motion.div>

        {/* ── Composer ── */}
        <motion.form
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          onSubmit={submitTopLevel}
          className="rm-cm-composer mt-12"
          data-testid="community-composer"
        >
          <label className="rm-cm-label">
            Your display name
            <input
              type="text"
              className="rm-cm-input"
              placeholder="What do you want to be called?"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={50}
              data-testid="community-display-name-input"
            />
          </label>
          <label className="rm-cm-label">
            Share a word
            <textarea
              className="rm-cm-textarea"
              placeholder="Testimony, a question, a prayer…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={2000}
              rows={5}
              data-testid="community-content-input"
            />
          </label>
          <div className="rm-cm-composer-foot">
            <span className="rm-cm-counter" data-testid="community-char-counter">
              {trimmedContent.length} / 2000
            </span>
            <button
              type="submit"
              className="rm-btn-solid"
              disabled={!canPost}
              data-testid="community-post-btn"
            >
              {posting ? "Posting…" : "Post to the room"}
            </button>
          </div>
        </motion.form>

        {error && (
          <div
            className="rm-cm-alert rm-cm-alert-error mt-6"
            data-testid="community-error"
          >
            {error}
          </div>
        )}
        {info && (
          <div
            className="rm-cm-alert rm-cm-alert-info mt-6"
            data-testid="community-info"
          >
            {info}
          </div>
        )}

        {/* ── Thread list ── */}
        <div className="mt-14">
          <div
            className="font-cinzel text-[10px] sm:text-xs tracking-[0.32em] uppercase text-[#FFE7A0]/70 mb-6"
            data-testid="community-thread-label"
          >
            What the room is saying
          </div>
          {loading && (
            <div
              className="rm-cm-empty"
              data-testid="community-loading"
            >
              Loading the room…
            </div>
          )}
          {!loading && threads.length === 0 && (
            <div
              className="rm-cm-empty"
              data-testid="community-empty"
            >
              The room is quiet. Be the first to speak.
            </div>
          )}
          {!loading && threads.length > 0 && (
            <div className="rm-cm-threads" data-testid="community-threads-list">
              {threads.map((thread) => (
                <CommentNode
                  key={thread.id}
                  thread={thread}
                  displayName={trimmedName}
                  onReply={submitReply}
                  onFlag={flagComment}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
